import { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { syncConversationsMessages } from "actions/conversationActions";
import { sendMessage, sendImageMessage } from "actions/messageActions";
import { getShops } from "actions/shopMessageActions";
import { Link } from "react-router-dom";
import moment from "moment";
import Avatar from "react-avatar";
import cx from "classnames";
import { parseJson } from "components/Function";
import { Dropdown, DropdownItem, DropdownMenu } from "reactstrap";
import useWindowSize from "hook/useWindowSize";
import { debounce } from "lodash";

const Sidebar = ({
    getShops,
    syncConversationsMessages,
    onConversationSelect,
    setFilter,
    filter,
    conversation,
    onOpenSetting,
}) => {
    const { listconversations: list } = conversation;
    const [shops, setShops] = useState([]);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [heightHeader, setHeightHeader] = useState("100%");
    const { t } = useTranslation();
    const { height: windowH } = useWindowSize();

    useEffect(() => {
        const { height: heightHeader } = document.getElementById("kt_chat_contacts_header")?.getBoundingClientRect();
        const { height } = document.getElementById("message-main")?.getBoundingClientRect();
        setHeightHeader(`calc(${height - heightHeader}px - 1.25rem)`);
    }, [windowH]);

    useEffect(() => {
        getShops({ sizeperpage: 1000 }).then((data) => {
            setShops(data?.shops || []);
        });
    }, [getShops]);

    const searchDebounce = useCallback(
        debounce((e) => setFilter((prev) => ({ ...prev, search: e.target.value })), 300),
        []
    );

    const onChange = (e) => {
        searchDebounce(e);
    };

    const getLastMessage = (conversation) => {
        const message = parseJson(conversation?.latest_message?.content);
        if (message) {
            if (message.content || typeof message?.content === "string") return message.content;
            return "File";
        }
    };

    const onClickSyncConversationsMessages = () => {
        syncConversationsMessages();
    };

    return (
        <div className="flex-column flex-lg-row-auto w-100 w-lg-300px w-xl-400px mb-10 mb-lg-0">
            <div className="card card-flush h-100">
                <div className="card-header pt-7" id="kt_chat_contacts_header" style={{ minHeight: "126px" }}>
                    <div className="w-100 position-relative">
                        <div className="row">
                            <div className="col-lg-9">
                                <i className="ki-outline ki-magnifier fs-3 text-gray-500 position-absolute top-50 ms-5 translate-middle-y"></i>
                                <input
                                    type="text"
                                    className="form-control form-control-solid px-13"
                                    name="search"
                                    placeholder="Search by username..."
                                    onChange={(e) => {
                                        onChange(e);
                                    }}
                                />
                            </div>
                            <div className="col-lg-3" style={{ textAlign: "right" }}>
                                <button className="btn btn-danger" onClick={onOpenSetting}>
                                    <i className="ki-outline ki-setting fs-2 m-0 p-0"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="w-100 d-flex gap-4 mt-4">
                        <div className="w-100">
                            <button className="btn btn-info w-100" onClick={onClickSyncConversationsMessages}>
                                <i className="ki-outline ki-arrows-loop fs-2 m-0 pr-2"></i>
                                <span className="ms-1">Sync Tiktok</span>
                            </button>
                        </div>

                        <div className="w-100">
                            <button className="btn btn-info w-100" onClick={() => setDropdownOpen(!dropdownOpen)}>
                                <i className="ki-outline ki-home fs-2 m-0 pr-2"></i>
                                <span className="ms-1">View by shop</span>
                            </button>
                            <Dropdown isOpen={dropdownOpen} toggle={() => setDropdownOpen(!dropdownOpen)}>
                                <DropdownMenu className="w-100">
                                    {shops.map((shop) => (
                                        <DropdownItem
                                            active={shop.apiShopId === filter.shops}
                                            onClick={() =>
                                                setFilter({
                                                    shops: shop.apiShopId === filter.shops ? "" : shop.apiShopId,
                                                })
                                            }
                                            key={shop._id}
                                        >
                                            {shop.name}
                                        </DropdownItem>
                                    ))}
                                </DropdownMenu>
                            </Dropdown>
                        </div>
                    </div>
                </div>
                <div
                    className="card-body pt-5"
                    id="kt_chat_contacts_body"
                    style={{ maxHeight: heightHeader, overflowY: "auto" }}
                >
                    <div className="h-lg-auto" style={{ maxHeight: "100%", overflowY: "auto" }}>
                        <div className="separator separator-dashed d-none"></div>
                        {list.map((item, key) => (
                            <div
                                className={cx("d-flex flex-stack py-4 cursor-pointer p-3 gap-2", {
                                    "bg-secondary": false,
                                })}
                                key={item._id}
                                onClick={() => onConversationSelect(item)}
                            >
                                <div className="d-flex align-items-center gap-4">
                                    <div className="symbol symbol-45px symbol-circle">
                                        {item?.participants?.[1]?.avatar ? (
                                            <img src={item?.participants?.[1]?.avatar} alt="" />
                                        ) : (
                                            <Avatar name={item?.participants?.[1]?.nickname} size="45" round={true} />
                                        )}
                                        <div className="symbol-badge bg-success start-100 top-100 border-4 h-8px w-8px ms-n2 mt-n2"></div>
                                    </div>
                                    <div>
                                        <Link
                                            to="#"
                                            className="fs-5 fw-bold text-gray-900 text-hover-primary ellipsis"
                                            style={{ maxWidth: "70%" }}
                                        >
                                            {item?.participants?.[1]?.nickname || t("New Conversation")}
                                        </Link>
                                        <div
                                            className="fw-semibold text-muted"
                                            style={{
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                display: "-webkit-box",
                                                WebkitBoxOrient: "vertical",
                                                WebkitLineClamp: 1,
                                            }}
                                        >
                                            {getLastMessage(item)}
                                        </div>
                                    </div>
                                </div>
                                <div className="d-flex flex-column align-items-end ms-2" style={{ minWidth: "85px" }}>
                                    <span className="text-muted fs-7 mb-1 text-end">
                                        {moment
                                            .unix(Math.floor(+(item?.latest_message?.create_time || Date.now() / 1000)))
                                            .fromNow()}
                                    </span>
                                    <div>
                                        <span
                                            className="badge badge-sm badge-circle badge-light-danger"
                                            data-tooltip-id="my-tooltip"
                                            data-tooltip-content={t("Unread")}
                                        >
                                            {item.unread_count > 0 && item.unread_count}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state) => ({
    conversation: state.conversation,
    message: state.message,
    shop: state.shop,
});
const mapDispatchToProps = {
    syncConversationsMessages,
    sendMessage,
    sendImageMessage,
    getShops,
};

export default connect(mapStateToProps, mapDispatchToProps)(Sidebar);
